import React, { useEffect, useState } from 'react';
import { researchNavigate, useResearchLocation } from '../desk/researchNavigation';
import './brief.css';

/* ---------- small helpers ---------- */

function md(text) {
  if (typeof text !== 'string') return text;
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

function slugify(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function cell(value) {
  if (value == null) return '—';
  if (Array.isArray(value)) return value.map(v => (typeof v === 'object' ? '' : String(v))).filter(Boolean).join(', ') || '—';
  if (typeof value === 'object') return '';
  return String(value);
}

function GradePill({ grade }) {
  if (!grade) return null;
  const band = String(grade).trim().charAt(0).toUpperCase();
  const cls = band === 'A' ? 'mb-grade-a' : band === 'B' ? 'mb-grade-b' : band === 'C' ? 'mb-grade-c' : 'mb-grade-d';
  return <span className={`mb-grade ${cls}`} title="Grade">{grade}</span>;
}

function ResultPill({ result }) {
  if (!result) return null;
  return <span className={`mb-result mb-result-${result}`}>{result}</span>;
}

function levelStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (/target hit/.test(s)) return 'hit';
  if (/invalidated|stop hit/.test(s)) return 'miss';
  return 'open';
}

function Callout({ callout }) {
  if (!callout) return null;
  const label = callout.label || 'NOTE';
  return <div className="mb-callout"><div className="mb-callout-label">{label}</div><div>{md(callout.text)}</div></div>;
}

function PullQuotes({ quotes }) {
  if (!quotes || !quotes.length) return null;
  return <div>{quotes.map((q, i) => <blockquote key={i} className="mb-quote">{md(q.text)}<span className="mb-quote-src">{q.source_record_id}</span></blockquote>)}</div>;
}

function ChartPh({ chart }) {
  if (!chart) return null;
  return <figure className="mb-chart"><div className="mb-chart-ph">
    <div className="mb-chart-ph-title">{chart.title || 'Chart'}</div>
    {chart.caption ? <p>{chart.caption}</p> : <p>Chart rendering coming soon.</p>}
  </div></figure>;
}

function DictTable({ rows, caption, monoCols }) {
  if (!rows || !rows.length) return null;
  const cols = [...new Set(rows.flatMap(r => Object.keys(r || {})))].filter(c => !/^(svg|chart)$/i.test(c));
  if (!cols.length) return null;
  return <div className="mb-table-wrap"><table className="mb-table">
    {caption && <caption>{caption}</caption>}
    <thead><tr>{cols.map(c => <th key={c} scope="col" className={monoCols ? 'mb-num' : ''}>{c.replace(/_/g, ' ')}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i}>{cols.map(c => <td key={c} className={monoCols ? 'mb-num' : ''}>{cell(r[c])}</td>)}</tr>)}</tbody>
  </table></div>;
}

function ParaList({ items }) {
  if (!items || !items.length) return null;
  return <div>{items.map((p, i) => <p key={i}>{md(p)}</p>)}</div>;
}

/* ---------- page renderers ---------- */

function CoverView({ page, issue }) {
  const m = issue.masthead || {};
  return <section className="mb-section" id="mb-s-0" aria-label="Cover">
    <div className="mb-masthead">
      <span className="mb-brand">{m.brand || "Carlos's Market Brief"}</span>
      <span>{m.left}</span><span>{m.center}</span><span>{m.right}</span>
    </div>
    <div className="mb-cover-kicker">{issue.tier ? `${issue.tier} · ${issue.date}` : issue.date}</div>
    <h1>{page.headline}</h1>
    <p className="mb-dek">{md(page.dek)}</p>
    <div className="mb-tiles">{(page.stat_tiles || []).map((t, i) => <div key={i} className="mb-tile">
      <div className="mb-tile-value">{t.value}</div><div className="mb-tile-caption">{t.caption}</div>
    </div>)}</div>
    {page.bottom_line && <div className="mb-callout"><div className="mb-callout-label">{page.bottom_line.label}</div><div>{md(page.bottom_line.text)}</div></div>}
    {(page.odds_bars || []).length > 0 && <div className="mb-odds">{page.odds_bars.map((b, i) => {
      const latest = (b.latest || []).filter(Number.isFinite);
      const width = latest.length ? Math.max(0, Math.min(100, Math.max(...latest))) : 0;
      return <div key={i} className="mb-odds-row">
        <span className="mb-odds-label">{b.label}</span><span className="mb-odds-value">{b.value}</span>
        <div className="mb-odds-track">
          <div className="mb-odds-latest" style={{ width: `${width}%` }} />
          {(b.earlier || []).filter(Number.isFinite).map((v, j) => <span key={j} className="mb-odds-earlier" style={{ left: `${Math.max(0, Math.min(100, v))}%` }} />)}
        </div>
      </div>;
    })}</div>}
    {((page.bear_case || []).length > 0 || (page.bull_case || []).length > 0) && <div className="mb-cases">
      {(page.bear_case || []).length > 0 && <div className="mb-case mb-case-bear"><span className="mb-case-label">Bear case</span><ul>{page.bear_case.map((b, i) => <li key={i}>{md(b)}</li>)}</ul></div>}
      {(page.bull_case || []).length > 0 && <div className="mb-case mb-case-bull"><span className="mb-case-label">Bull case</span><ul>{page.bull_case.map((b, i) => <li key={i}>{md(b)}</li>)}</ul></div>}
    </div>}
    {(page.inside_this_issue || []).length > 0 && <nav className="mb-inside" aria-label="Inside this issue">
      <div className="mb-inside-label">Inside this issue</div>
      <ul>{page.inside_this_issue.map((l, i) => {
        const target = resolveSection(l, issue.pages);
        return <li key={i}>{target >= 0 ? <a href={`#mb-s-${target}`}>{l.label}</a> : <span>{l.label}</span>}{l.line && <span> — {l.line}</span>}</li>;
      })}</ul>
    </nav>}
  </section>;
}

function resolveSection(link, pages) {
  const sid = String(link.section_id ?? '');
  if (/^\d+$/.test(sid)) { const i = Number(sid); if (pages[i]) return i; }
  const slug = slugify(sid);
  const i = pages.findIndex((p, idx) => slugify(p.tag) === slug || `mb-s-${idx}` === sid);
  return i;
}

function SectionShell({ page, index, children }) {
  return <section className="mb-section" id={`mb-s-${index}`} aria-label={page.tag}>
    <span className="mb-pill">{page.tag}</span>
    <hr className="mb-rule" />
    {children}
  </section>;
}

function PageView({ page, index, issue }) {
  switch (page.page_type) {
    case 'cover': return <CoverView page={page} issue={issue} />;
    case 'overview':
      return <SectionShell page={page} index={index}><ParaList items={page.paragraphs} /></SectionShell>;
    case 'market_overview':
      return <SectionShell page={page} index={index}><h2>{page.title}</h2><ParaList items={page.paragraphs} />{(page.charts || []).map((c, i) => <ChartPh key={i} chart={c} />)}</SectionShell>;
    case 'timeline':
      return <SectionShell page={page} index={index}>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Window</th><th scope="col">What happened</th><th scope="col">Read</th></tr></thead>
        <tbody>{page.rows.map((r, i) => <tr key={i}><td className="mb-num">{r.window}</td><td>{md(r.what_happened)}</td><td>{md(r.read)}</td></tr>)}</tbody></table></div>
        {(page.stat_tiles || []).length > 0 && <div className="mb-tiles">{page.stat_tiles.map((t, i) => <div key={i} className="mb-tile"><div className="mb-tile-value">{t.value}</div><div className="mb-tile-caption">{t.caption}</div></div>)}</div>}
        {page.paragraph && <p>{md(page.paragraph)}</p>}
      </SectionShell>;
    case 'levels_table':
      return <SectionShell page={page} index={index}>
        <p className="mb-mono" style={{ fontSize: 13, color: '#6B7280' }}>Desk read · {page.as_of_time}</p>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Asset</th><th scope="col">Price</th><th scope="col">Bias</th><th scope="col">Resistance</th><th scope="col">Support</th><th scope="col">Book imbalance</th></tr></thead>
        <tbody>{page.rows.map((r, i) => <tr key={i}><td className="mb-num"><strong>{r.asset}</strong></td><td className="mb-num">{r.price}</td><td>{r.bias}</td><td className="mb-num">{r.resistance}</td><td className="mb-num">{r.support}</td><td className="mb-num">{r.book_imbalance || '—'}</td></tr>)}</tbody></table></div>
        {page.chart && <ChartPh chart={page.chart} />}
        {page.venue_check && <p><em>{md(page.venue_check)}</em></p>}
      </SectionShell>;
    case 'cross_asset':
      return <SectionShell page={page} index={index}>
        <p>{md(page.intro)}</p>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Asset</th><th scope="col">Level</th><th scope="col">Read</th></tr></thead>
        <tbody>{page.rows.map((r, i) => <tr key={i}><td><strong>{r.asset}</strong></td><td className="mb-num">{r.level}</td><td>{md(r.read)}</td></tr>)}</tbody></table></div>
        {page.paragraph && <p>{md(page.paragraph)}</p>}
        {page.cases && <div className="mb-cases">
          {page.cases.bull && <div className="mb-case mb-case-bull"><span className="mb-case-label">{page.cases.bull.label || 'Bull'}</span><h4 className="mb-h4" style={{ marginTop: 0 }}>{md(page.cases.bull.title)}</h4><ul>{(page.cases.bull.bullets || []).map((b, i) => <li key={i}>{md(b)}</li>)}</ul></div>}
          {page.cases.bear && <div className="mb-case mb-case-bear"><span className="mb-case-label">{page.cases.bear.label || 'Bear'}</span><h4 className="mb-h4" style={{ marginTop: 0 }}>{md(page.cases.bear.title)}</h4><ul>{(page.cases.bear.bullets || []).map((b, i) => <li key={i}>{md(b)}</li>)}</ul></div>}
        </div>}
      </SectionShell>;
    case 'verdicts':
      return <SectionShell page={page} index={index}>
        <p>{md(page.intro)}</p>
        {page.rows.map((r, i) => <div key={i} className="mb-verdict mb-card">
          <div><span className="mb-verdict-name">{r.name}</span> <span className="mb-verdict-affil">· {r.affiliation}</span></div>
          <p style={{ margin: '8px 0 0' }}>{md(r.verdict)}</p>
        </div>)}
      </SectionShell>;
    case 'remarks':
      return <SectionShell page={page} index={index}>
        <PullQuotes quotes={page.pull_quotes} />
        {(page.items || []).map((it, i) => <div key={i} className="mb-field"><span>{it.label}</span><p>{md(it.text)}</p></div>)}
        <Callout callout={page.callout} />
      </SectionShell>;
    case 'qa_bullets':
      return <SectionShell page={page} index={index}>
        <h3 className="mb-h3">{page.heading}</h3>
        {(page.bullets || []).map((b, i) => <div key={i} className="mb-field"><span>{b.label}</span><p>{md(b.text)}</p></div>)}
      </SectionShell>;
    case 'event_decision':
      return <SectionShell page={page} index={index}>
        <p>{md(page.intro)}</p>
        {(page.cards || []).map((c, i) => <div key={i} className="mb-card"><div className="mb-card-head">{c.title}</div><ul>{(c.bullets || []).map((b, j) => <li key={j}>{md(b)}</li>)}</ul></div>)}
        <PullQuotes quotes={page.pull_quotes} />
        <ParaList items={page.paragraphs} />
        {(page.charts || []).map((c, i) => <ChartPh key={i} chart={c} />)}
      </SectionShell>;
    case 'reads_evolution':
      return <SectionShell page={page} index={index}>
        <DictTable rows={page.rows} monoCols />
        {page.chart && <ChartPh chart={page.chart} />}
        <Callout callout={page.takeaway} />
      </SectionShell>;
    case 'leg':
      return <SectionShell page={page} index={index}>
        <h2>Leg {page.number}: {page.title}<GradePill grade={page.grade} /></h2>
        {(page.charts || []).map((c, i) => <ChartPh key={i} chart={c} />)}
        {(page.blocks || []).map((b, i) => <LegBlock key={i} block={b} />)}
        <Callout callout={page.callout} />
        <p><strong>Assessment:</strong> {md(page.assessment)}</p>
      </SectionShell>;
    case 'positions':
      return <SectionShell page={page} index={index}>
        <h2>{page.title}</h2>
        {page.intro && <p>{md(page.intro)}</p>}
        {(page.items || []).map((it, i) => <PositionItem key={i} item={it} />)}
        {page.board_note && <p><em>{md(page.board_note)}</em></p>}
      </SectionShell>;
    case 'scenarios':
      return <SectionShell page={page} index={index}>
        <p>{md(page.intro)}</p>
        {page.paths.map((p, i) => <div key={i} className="mb-card mb-path">
          <div className="mb-path-head"><h4>{p.name}</h4><span className="mb-path-prob">{p.probability}%</span></div>
          {p.subtitle && <p style={{ color: '#6B7280', fontSize: 14 }}>{p.subtitle}</p>}
          <p style={{ margin: 0 }}>{md(p.text)}</p>
        </div>)}
        {page.positioning_summary && <Callout callout={{ label: 'Positioning summary', text: page.positioning_summary }} />}
      </SectionShell>;
    case 'invalidation':
      return <SectionShell page={page} index={index}>
        {(page.tripwires || []).map((t, i) => <div key={i} className="mb-card">
          <div className="mb-card-head">{t.label} — {t.title}</div><p style={{ margin: '8px 0 0' }}>{md(t.text)}</p>
        </div>)}
        <ParaList items={page.conclusion_paragraphs} />
        <p><strong>Overall grade</strong><GradePill grade={page.grade} /></p>
      </SectionShell>;
    case 'whats_changed':
      return <SectionShell page={page} index={index}>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">What</th><th scope="col">Then</th><th scope="col">Now</th><th scope="col">Why it matters</th></tr></thead>
        <tbody>{page.items.map((it, i) => <tr key={i}><td><strong>{it.what}</strong></td><td>{md(it.then)}</td><td>{md(it.now)}</td><td>{md(it.why_it_matters)}</td></tr>)}</tbody></table></div>
      </SectionShell>;
    case 'levels_check':
      return <SectionShell page={page} index={index}>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Ticker</th><th scope="col">Price</th><th scope="col">Trigger</th><th scope="col">Trade stop</th><th scope="col">Invalidation</th><th scope="col">Status</th></tr></thead>
        <tbody>{page.rows.map((r, i) => <tr key={i}><td className="mb-num"><strong>{r.ticker}</strong></td><td className="mb-num">{r.price}</td><td className="mb-num">{r.trigger}</td><td className="mb-num">{r.trade_stop}</td><td className="mb-num">{r.invalidation}</td><td><ResultPill result={levelStatusClass(r.status)} /> <span style={{ fontSize: 13 }}>{r.status}</span></td></tr>)}</tbody></table></div>
      </SectionShell>;
    case 'catalysts':
      return <SectionShell page={page} index={index}>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Date</th><th scope="col">Time CT</th><th scope="col">Event</th><th scope="col">Consensus</th><th scope="col">Why it matters</th></tr></thead>
        <tbody>{page.rows.map((r, i) => <tr key={i}><td className="mb-num">{r.date}</td><td className="mb-num">{r.time_ct}</td><td><strong>{r.event}</strong></td><td>{r.consensus || '—'}</td><td>{md(r.why_it_matters)}</td></tr>)}</tbody></table></div>
      </SectionShell>;
    case 'next':
      return <SectionShell page={page} index={index}>
        <div className="mb-next-grid">{(page.cards || []).map((c, i) => <div key={i} className="mb-next-card">
          <div className="mb-when">{c.when_label}</div><h4>{c.title}</h4><p>{md(c.text)}</p>
        </div>)}</div>
        {page.scenario_matrix && <ScenarioMatrix matrix={page.scenario_matrix} />}
      </SectionShell>;
    case 'scorecard':
      return <SectionShell page={page} index={index}>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Call</th><th scope="col">Level</th><th scope="col">Result</th><th scope="col">Note</th></tr></thead>
        <tbody>{page.rows.map((r, i) => <tr key={i}><td><strong>{md(r.call)}</strong></td><td className="mb-num">{r.level || '—'}</td><td><ResultPill result={r.result} /></td><td>{r.note ? md(r.note) : '—'}</td></tr>)}</tbody></table></div>
      </SectionShell>;
    case 'close':
      return <SectionShell page={page} index={index}>
        <ParaList items={page.paragraphs} />
        <Callout callout={page.callout} />
        {page.sources_text && <p style={{ fontSize: 14, color: '#6B7280' }}>{page.sources_text}</p>}
      </SectionShell>;
    case 'sources':
      return <SectionShell page={page} index={index}>
        <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Provider</th><th scope="col">Used for</th></tr></thead>
        <tbody>{(page.groups || []).map((g, i) => <tr key={i}><td><strong>{g.provider || g.name || '—'}</strong></td><td>{md(g.used_for || g.description || '')}</td></tr>)}</tbody></table></div>
      </SectionShell>;
    default:
      return <SectionShell page={page} index={index}><p style={{ color: '#6B7280' }}><em>Section type “{page.page_type}” is not rendered yet.</em></p></SectionShell>;
  }
}

function LegBlock({ block }) {
  if (!block || typeof block !== 'object') return null;
  if (block.subheading || block.heading) return <h4 className="mb-h4">{md(block.subheading || block.heading)}</h4>;
  if (Array.isArray(block.bullets)) return <ul>{block.bullets.map((b, i) => <li key={i}>{typeof b === 'string' ? md(b) : <><strong>{b.label}: </strong>{md(b.text)}</>}</li>)}</ul>;
  if (Array.isArray(block.items)) return <ul>{block.items.map((b, i) => <li key={i}>{typeof b === 'string' ? md(b) : <><strong>{b.label}: </strong>{md(b.text)}</>}</li>)}</ul>;
  if (block.paragraph) return <p>{md(block.paragraph)}</p>;
  if (block.text) return <p>{md(block.text)}</p>;
  if (Array.isArray(block.paragraphs)) return <ParaList items={block.paragraphs} />;
  if (block.callout) return <Callout callout={block.callout} />;
  return null;
}

function PositionItem({ item }) {
  if (!item) return null;
  if (item.tickers || item.levels) {
    const dir = (item.direction || '').toUpperCase();
    return <div className="mb-card">
      <div className="mb-card-head">{(item.tickers || []).join(' / ')} <span className="mb-price">{item.price_line}</span></div>
      <div className="mb-card-tagline">{item.tagline} {dir && <span className="mb-tier-pill">{dir}</span>} {item.desk_read && <span className="mb-tier-pill" style={{ background: '#EEE9E0', color: '#1F2328' }}>{item.desk_read}</span>}</div>
      <div className="mb-field"><span>Thesis</span><p>{md(item.thesis)}</p></div>
      <div className="mb-field mb-field-risk"><span>Risk</span><p>{md(item.risk)}</p></div>
      <div className="mb-field"><span>Scenario</span><p>{md(item.scenario)}</p></div>
      {item.levels && <div className="mb-levels"><dl>
        <dt>Trigger</dt><dd>{item.levels.trigger}</dd>
        <dt>Trade stop</dt><dd>{item.levels.trade_stop}</dd>
        <dt>Invalidation</dt><dd>{item.levels.invalidation}</dd>
        {(item.levels.targets || []).map((t, i) => <React.Fragment key={i}><dt>Target {i + 1}</dt><dd>{t.level}{t.source && <span style={{ color: '#6B7280' }}> · {t.source}</span>}</dd></React.Fragment>)}
      </dl></div>}
    </div>;
  }
  if (item.chart) return <ChartPh chart={item.chart} />;
  return null;
}

function ScenarioMatrix({ matrix }) {
  const cols = matrix.columns || [];
  const rows = matrix.rows || [];
  if (!cols.length || !rows.length) return null;
  return <div style={{ marginTop: 20 }}>
    {matrix.title && <h4 className="mb-h4">{matrix.title}</h4>}
    {matrix.consensus_line && <p style={{ color: '#6B7280', fontSize: 14 }}>{matrix.consensus_line}</p>}
    <DictTable rows={rows.map(r => { const o = {}; cols.forEach((c, i) => { o[c] = Array.isArray(r) ? r[i] : (r[c] ?? r[Object.keys(r)[i]]); }); return o; })} />
  </div>;
}

/* ---------- full issue ---------- */

export function BriefIssue({ issue }) {
  if (!issue) return <div className="mb-empty">No issue to display.</div>;
  const pages = issue.pages || [];
  const tocPages = pages.map((p, i) => ({ p, i })).filter(({ p }) => p.page_type !== 'cover');
  const revisions = issue.revisions || [];
  const latestRev = revisions[revisions.length - 1];
  const stance = issue.stance || {};
  return <div className="mb-col">
    <nav className="mb-toc" aria-label="Issue sections">
      {tocPages.map(({ p, i }) => <a key={i} href={`#mb-s-${i}`}>{p.tag}</a>)}
    </nav>
    <details className="mb-jump"><summary>Jump to a section</summary>
      <ul>{tocPages.map(({ p, i }) => <li key={i}><a href={`#mb-s-${i}`}>{p.tag}</a></li>)}</ul>
    </details>

    {issue.event && <div className="mb-update-note"><strong>Event report</strong> — {issue.event.name} · {issue.event.time_ct}</div>}
    {latestRev && <div className="mb-update-note"><strong>Updated</strong> — {latestRev.updated_at}{latestRev.note ? ` · ${latestRev.note}` : ''}</div>}

    <div className="mb-stance" aria-label="Market stance">
      <span className={`mb-stance-dir mb-stance-${stance.direction || 'neutral'}`}>{stance.direction || '—'}</span>
      {stance.conviction && <span><small>Conviction</small> <strong>{stance.conviction}</strong></span>}
      {stance.grade && <span><small>Grade</small> <GradePill grade={stance.grade} /></span>}
      {stance.changed_from_prior && <span><small>Changed from prior</small> <strong>{stance.change_reason || 'see overview'}</strong></span>}
    </div>

    {pages.map((page, i) => <PageView key={i} page={page} index={i} issue={issue} />)}

    {(issue.sources || []).length > 0 && <section className="mb-section" aria-label="Sources">
      <span className="mb-pill">Sources</span><hr className="mb-rule" />
      <div className="mb-table-wrap"><table className="mb-table"><thead><tr><th scope="col">Provider</th><th scope="col">Used for</th></tr></thead>
      <tbody>{issue.sources.map((s, i) => <tr key={i}><td><strong>{s.provider || s.name || '—'}</strong></td><td>{md(s.used_for || s.description || '')}</td></tr>)}</tbody></table></div>
    </section>}

    <footer className="mb-footer">
      <p>Market commentary, not investment advice.</p>
      <p className="mb-mono" style={{ fontSize: 12 }}>Snapshot {issue.snapshot_id} · Updated {issue.updated_at}{issue.number ? ` · Issue No. ${issue.number}` : ''}</p>
      <p style={{ fontSize: 12 }}>Levels are snapshots and move — recheck before acting.</p>
    </footer>
  </div>;
}

/* ---------- page wrapper: fetch by slug ---------- */

export default function BriefIssuePage() {
  const path = useResearchLocation();
  const slug = decodeURIComponent(path.replace(/^\/research\/brief\//, '').split('/')[0] || '');
  const [issue, setIssue] = useState(null);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true); setError('');
        const [res, listRes] = await Promise.all([
          fetch(`/api/research/brief/issues/${encodeURIComponent(slug)}`, { signal: controller.signal, headers: { "X-API-Key": process.env.REACT_APP_API_KEY || "" } }),
          fetch('/api/research/brief/issues?limit=100', { signal: controller.signal, headers: { "X-API-Key": process.env.REACT_APP_API_KEY || "" } }),
        ]);
        if (res.status === 404) throw new Error('Issue not found.');
        if (!res.ok) throw new Error('The brief store is unavailable. Please retry.');
        const data = await res.json();
        let prev = null, next = null;
        if (listRes.ok) {
          const list = (await listRes.json()).issues || [];
          const idx = list.findIndex(it => it.slug === slug);
          if (idx >= 0) { next = list[idx - 1] || null; prev = list[idx + 1] || null; }
        }
        if (!disposed) { setIssue(data); setNeighbors({ prev, next }); }
      } catch (e) {
        if (!disposed && e.name !== 'AbortError') setError(e.message || 'Failed to load the issue.');
      } finally {
        if (!disposed) setLoading(false);
      }
    })();
    return () => { disposed = true; controller.abort(); };
  }, [slug]);

  return <div className="mb">
    <div className="mb-col" style={{ paddingBottom: 0 }}>
      <button type="button" className="mb-btn" onClick={() => researchNavigate('/research/brief')}>← All briefs</button>
    </div>
    {loading && <div className="mb-col"><div className="mb-empty">Loading the brief…</div></div>}
    {error && <div className="mb-col"><div className="mb-error">{error}</div></div>}
    {!loading && !error && <BriefIssue issue={issue} />}
    {!loading && !error && issue && <div className="mb"><div className="mb-col" style={{ paddingTop: 0 }}>
      <div className="mb-pager">
        <button type="button" disabled={!neighbors.prev} onClick={() => neighbors.prev && researchNavigate(`/research/brief/${neighbors.prev.slug}`)}>← {neighbors.prev ? neighbors.prev.title : 'No older issue'}</button>
        <button type="button" disabled={!neighbors.next} onClick={() => neighbors.next && researchNavigate(`/research/brief/${neighbors.next.slug}`)}>{neighbors.next ? neighbors.next.title : 'No newer issue'} →</button>
      </div>
    </div></div>}
  </div>;
}
